import {PluginHelper} from "../Plugin";
import {CallableDisposableStack, StackDisposable} from "../lib/disposable";
import {signal} from "../lib";
import $ from "jquery";
import {arraySignal, Fn} from "../lib/signal";
import {EL} from "../util/DomUtils";
import {HookCallback} from "../lib/hook";
import {DomWindow, DomWindowBounds} from "../DomWindow";
import {IHierarchy} from "../lib/hierarchy";
import {ActivityHolder} from "./ActivityHolder";

/**
 * an activity / activityholder should throw this error in case
 * it doesn't want to navigate, but this URL could probably be navigated
 * elsewhere.
 *
 * as opposed to CancelledError, which should be thrown if the user cancelled
 * navigation, and the navigation shouldn't be tried elsewhere.
 */
export class GoAwayError extends Error {}

/**
 * an activity / activityholder should throw this error in case
 * it isn't able to navigate, a new instance of the activity should
 * be created to navigate (immutable activities)
 */
export class MustRecreateError extends Error {}

export type NavigationOptions = {
    // Memo.navigate
    focus?: boolean,

    // Memo.navigate + App.navigate
    keepScrollPos?: boolean,

    // App.navigate
    openPopup?: boolean,

    // Window::navigate
    preferredTarget?: ActivityHolder,
    id?: string,
    defaultBounds?: DomWindowBounds,

    /**
     * if a window is once navigated with pinned:true, future navigation only succeeds if pinned:true
     */
    pinned?: boolean,

    contentUrl?: URL,

    // ActivityHolder.onNavigate
    refresh?: boolean,

    userAction?: 'urlentry' | 'click' | 'history',

    cancel?: AbortSignal,
};

export type OnNavigateOptions = NavigationOptions & {
    previousUrl: URL | null,
};

export type ActivityConstructorParams = {
    activityUrl?: URL,
};

export interface IActivity extends IHierarchy, Disposable {
    onNavigate(content: object, options: OnNavigateOptions): Promise<void>;
    onBeforeNavigate(content: object, options: OnNavigateOptions): Promise<void>;
    onBeforeUnload(): Promise<void>;
    initUI(): Promise<void>;
    attachToHost(host: IActivityHost): void;
    afterResize(): void;
}

export interface IActivityHost extends IHierarchy {
    setTitle(title: string): void;
    setContent(content: HTMLElement): void;
    setButtons(buttons: HTMLButtonElement[]): void;
    close(): void;
    navigate(url: URL, navigationOptions?: NavigationOptions): Promise<void>;
}

const activityShadowCSS = new CSSStyleSheet();
activityShadowCSS.replaceSync(`
.windowContent { overflow: auto; flex: 1; background: rgb(250, 250, 250) linear-gradient(rgb(254, 250, 253) 0%, rgb(229, 224, 228) 100%); }
`);

/*type SubclassOfActivity =
    (new () => Activity) &
    { [K in keyof typeof Activity]: typeof Activity[K] }*/
@PluginHelper.Mixin @StackDisposable.Mixin
export /*abstract*/ class Activity implements StackDisposable, PluginHelper, IActivity {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    //static activityTypes = arraySignal<() => ((SubclassOfActivity) | { name: string, protocol?: string, getConstructor(): Promise<SubclassOfActivity> })[]>();
    static activityTypes = arraySignal<() => ((typeof Activity) | { name: string, protocol?: string, getConstructor(): Promise<typeof Activity> })[]>();

    static protocol : string | null = null;

    shadowRoot: ShadowRoot;
    rootContainerEl: HTMLDivElement;
    contentEl: HTMLDivElement;
    footerEl: HTMLDivElement;
    props: object = {};
    propsChanged = signal<(updatedKeys?: (string|Symbol)[]) => void>();
    get $content () { return $(this.contentEl); }
    get $footer () { return $(this.footerEl); }
    protected title: string;

    static styleSheets = [];

    parent: IActivityHost | null = null;

    get children(): IHierarchy[] {
        return [];
    }

    async navigate(url: URL, navigationOptions?: NavigationOptions) {
        await this.parent.navigate(url, navigationOptions);
    }
    //abstract onNavigate(url: URL, options: OnNavigateOptions): Promise<void>;
    async onNavigate(content: object, navigationOptions: NavigationOptions) { /* do nothing - subclasses don't need to call super */ }
    constructor(params: ActivityConstructorParams) {
        this.rootContainerEl = EL('div', { class: 'activityRoot', style: {display: 'flex', flex: '1', flexDirection: 'column', background: 'white', overflow: 'hidden'} });
        if (this.useShadowRoot()) {
            this.shadowRoot = this.rootContainerEl.shadowRoot || this.rootContainerEl.attachShadow({mode: 'open'});
            this.shadowRoot.replaceChildren(
                this.contentEl = EL('div', {class: 'windowContent'}),
                this.footerEl = EL('div', {class: 'windowFooter'}),
            );
            this.shadowRoot.adoptedStyleSheets = [activityShadowCSS].concat((this.constructor as typeof Activity).styleSheets);
        } else {
            this.rootContainerEl.replaceChildren(
                this.contentEl = EL('div', {class: 'windowContent', style: {flex: '1'}}),
                this.footerEl = EL('div', {class: 'windowFooter'}),
            );
        }
    }

    async onBeforeNavigate(content: object, options: OnNavigateOptions): Promise<void> {
    }
    async onBeforeUnload(): Promise<void> {
    }

    protected useShadowRoot() {
        return true;
    }
    //abstract initUI(): Promise<void>;
    async initUI() { /* do nothing - subclasses don't need to call super */ }
    attachToHost(host: IActivityHost) {
        this.parent = host;
        host?.setContent(this.rootContainerEl);
        host?.setButtons(this._buttons);
        if (host) this.afterResize();
    }

    private _buttons: HTMLButtonElement[] = [];
    protected addTitleButton(txt: string|{title: string, icon: string}, click: EventListener, right: boolean = false, hotkey?: string): HTMLButtonElement {
        const btn = DomWindow.makeButton(txt, click, right, hotkey);
        this._buttons.push(btn);
        if (this.parent) this.parent.setButtons(this._buttons);
        return btn;
    }
    protected setButtons(buttons: HTMLButtonElement[]) {
        this._buttons = buttons;
        if (this.parent) this.parent.setButtons(buttons);
    }

    protected setTitle(title: string) {
        this.title = title;
        if (this.parent) this.parent.setTitle(title);
    }
    afterResize() {}

    slot(object: EventTarget, signalName : string, handler : EventListener, callWithArgs?: any[]): EventListener;
    slot<F extends (...args: any) => any>(object: object, signalName : string | Function, handler : F, callWithArgs?: Parameters<F>): F;
    slot(object: EventTarget | object, signalName : string | Function, handler : Function, callWithArgs?: any[]): Function { throw new Error('Method not implemented.'); }
    hook<MN extends string|number|symbol, P extends any[], R>(object: object & Record<MN, Fn<P, R>>, method: MN, handler: HookCallback<P, R>, callWithArgs?: any[]) { throw new Error('Method not implemented.'); }
}

export class ImmutableActivity extends Activity {
    private isLoaded = false;

    async load(content: object, navigationOptions: NavigationOptions) {
    }

    async onBeforeNavigate(content: object, options: OnNavigateOptions): Promise<void> {
        if (this.isLoaded) throw new MustRecreateError();
    }

    async onNavigate(content: object, navigationOptions: NavigationOptions) {
        this.isLoaded = true;
        await this.load(content, navigationOptions);
    }

}
